import { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const AssetScheduler = ({ asset, onBookingComplete, isExpanded, onToggle }) => {
  const [bookedDates, setBookedDates] = useState([]);
  const [selectedRange, setSelectedRange] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isExpanded) {
      fetchAvailability();
    }
  }, [asset.asset_id, isExpanded]);

  const fetchAvailability = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/asset/${asset.asset_id}/availability`, {
        withCredentials: true
      });
      setBookedDates(response.data.booked_dates || []);
    } catch (error) {
      console.error('Error fetching availability:', error);
      // Don't show error toast as availability endpoint might not exist yet
      setBookedDates([]);
    }
  };

  // Calculate max date (90 days from today)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 90);

  const isDateBooked = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return bookedDates.some(booking => {
      const start = new Date(booking.start_date);
      const end = new Date(booking.end_date);
      const checkDate = new Date(dateStr);
      return checkDate >= start && checkDate <= end;
    });
  };

  const tileClassName = ({ date, view }) => {
    if (view === 'month') {
      // Check if date is outside 90-day range
      if (date > maxDate) {
        return 'opacity-30 cursor-not-allowed';
      }
      
      if (isDateBooked(date)) {
        // Booked by others - red square
        return 'booked-date';
      }
      
      // Available - blue square
      return 'available-date';
    }
  };

  const tileDisabled = ({ date, view }) => {
    if (view === 'month') {
      // Disable past dates, dates beyond 90 days, and booked dates
      return date < today || date > maxDate || isDateBooked(date);
    }
    return false;
  };

  const handleDateChange = (value) => {
    if (Array.isArray(value)) {
      setSelectedRange(value);
    } else {
      setSelectedRange([value, value]);
    }
  };

  const handleBooking = async () => {
    if (!selectedRange) {
      toast.error('Please select dates');
      return;
    }

    setLoading(true);
    try {
      const [startDate, endDate] = selectedRange;
      await axios.post(
        `${API_URL}/api/user-assets/${asset.id}/request-booking-dates`,
        {
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0]
        },
        { 
          withCredentials: true
        }
      );

      toast.success('Booking request submitted! Awaiting admin confirmation.');
      setSelectedRange(null);
      if (onBookingComplete) onBookingComplete();
      
      // Collapse the scheduler after successful request
      if (onToggle) onToggle();
    } catch (error) {
      console.error('Booking error:', error);
      // Properly extract error message
      let errorMessage = 'Booking request failed';
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Booking request failed';
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isExpanded) return null;

  return (
    <div className="space-y-4 mt-4">
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-semibold text-lg">Schedule Your Asset Usage</h4>
          <button
            onClick={onToggle}
            className="text-gray-500 hover:text-gray-700"
          >
            <ChevronUp size={20} />
          </button>
        </div>
        
        {/* Legend */}
        <div className="flex gap-4 mb-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-blue-500 rounded"></div>
            <span>Available</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-red-500 rounded bg-red-100"></div>
            <span>Booked</span>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-3">
          Select dates within the next 90 days for your asset usage
        </p>

        {/* Calendar */}
        <div className="calendar-container">
          <Calendar
            onChange={handleDateChange}
            value={selectedRange}
            selectRange={true}
            tileClassName={tileClassName}
            tileDisabled={tileDisabled}
            minDate={today}
            maxDate={maxDate}
            className="border-0 w-full"
          />
        </div>

        {/* Selected Range Display */}
        {selectedRange && (
          <div className="mt-4 p-3 bg-blue-50 rounded-md border border-blue-200">
            <p className="text-sm font-medium text-blue-900">Selected Dates:</p>
            <p className="text-sm text-blue-700">
              {selectedRange[0].toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} - {selectedRange[1].toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
            </p>
            <p className="text-xs text-blue-600 mt-1">
              {Math.ceil((selectedRange[1] - selectedRange[0]) / (1000 * 60 * 60 * 24)) + 1} day(s)
            </p>
          </div>
        )}

        <Button
          onClick={handleBooking}
          disabled={!selectedRange || loading}
          style={{ backgroundColor: '#D4AF37', color: '#000' }}
          className="w-full mt-4 hover:opacity-90 font-semibold transition-all duration-200"
        >
          {loading ? 'Submitting Request...' : 'Request Dates for Booking'}
        </Button>
      </div>

      <style jsx>{`
        .calendar-container {
          max-width: 100%;
        }
        .react-calendar {
          width: 100%;
          border: none;
          font-family: inherit;
        }
        .react-calendar__tile {
          padding: 0.75em 0.5em;
          border-radius: 4px;
          position: relative;
        }
        .react-calendar__tile--active {
          background: #D4AF37 !important;
          color: white !important;
        }
        .react-calendar__tile--now {
          background: #f3f4f6;
        }
        
        /* Available dates - blue square border */
        .available-date {
          border: 2px solid #3B82F6;
          background-color: #EFF6FF;
        }
        .available-date:hover:not(:disabled) {
          background-color: #DBEAFE;
        }
        
        /* Booked dates - red square border with red background */
        .booked-date {
          border: 2px solid #EF4444;
          background-color: #FEE2E2;
          color: #991B1B;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default AssetScheduler;
